<script setup>
  import { computed, onMounted, nextTick, ref, reactive } from 'vue'
  import { useFetch } from '@vueuse/core'
  import LottieAnimation from 'lottie-vuejs/src/LottieAnimation.vue'
  import AppInput from './AppInput.vue'
  import { changeLocale } from '@formkit/vue'
  import { useToken } from '@/composables/token'
  import { useCheckSpam } from '@/composables/checkSpam'

  const props = defineProps({
    type: {
      type: String,
      required: true
    },
    data: {
      type: String,
      required: true
    }
  })

  const currentLanguage = ref('de')

  const submitting = ref(false)
  const submitted = ref(false)
  const submitError = ref(false)
  const { getCSRFToken } = useToken()
  const { checkSpam } = useCheckSpam()

  // This is used to honeypot bots
  const userId = ref(null)

  const data = JSON.parse(props.data)

  const formSchema = reactive({
      ...data
  })

  const formData = reactive({})
  const errorHeadline = ref('Es ist ein Fehler aufgetreten')
  const errorMessage = ref('Bitte versuchen Sie es später erneut')
  const successHeadline = computed(() => 'Vielen Dank für Ihre Anfrage')
  const successMessage = computed(() => 'Wir werden uns so schnell wie möglich bei Ihnen melden')

  const submitAttrs = {
    inputClass: `
      bg-primary transition-opacity opacity-80 hover:opacity-100 rounded-full
      text-white mt-5 mr-auto py-5 px-14 tracking-wide text-sm font-semibold
      cursor-pointer`
  }

  const submitLabel = computed(() => 'Anfrage senden')

  onMounted(async () => {
    await nextTick()
    await changeLocale(currentLanguage.value)
  })

  const updateFormData = (name, value) => {
    formData[name] = value
  }

  const handleSubmit = async () => {
    if (userId.value && userId.value.length > 0) {
      alert('You are a bot!')
      return
    }
    submitting.value = true

    const isSpam = await checkSpam(formData)

    if (isSpam && isSpam.data.score <= 4) {
      submitting.value = false
      submitError.value = true

      errorHeadline.value = "Es ist ein Fehler aufgetreten"
      errorMessage.value = `
        Leider haben Ihre Eingaben die Sicherheitskriterien aus folgenden Gründen nicht erfüllt: <br>
        ${isSpam.data.reasons}
      `
      return
    }

    const { data, error } = await useFetch(`${window.location.origin}/api/v1/submit-form`, {
      async beforeFetch({ options, cancel }) {
        const { token, error } = await getCSRFToken()

        if (error || !token) {
          submitError.value = true
          submitting.value = false
          cancel()
          console.error(error)
          return error
        }

        options.headers = {
          ...options.headers,
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        }
        return {
          options
        }
      }
    }).post({
      webform_id: props.type,
      values: formData
    }).json()

    if (error.value) {
      submitError.value = true
      submitting.value = false
      console.error(error.value)
      return error.value
    }

    if (data.value.ok) {
      setTimeout(() => {
        submitting.value = false
        submitError.value = false
        submitted.value = true
      }, 2000)
    }
  }
</script>


<template>
  <div
    class="app-form-wrapper"
  >
      <div
        v-if="!submitting && !submitted && !submitError"
        class="form-inner"
      >
        <FormKit
          type="form"
          form-class="grid grid-cols-1 gap-5"
          :id="props.type"
          :submit-label=submitLabel
          :submit-attrs="submitAttrs"
          @submit="handleSubmit()"
        >
          <AppInput
            v-for="(field, key) in formSchema"
            @updateValue="updateFormData"
            :key="key"
            :data="field"
            :name="key"
          />
          <input
            v-model="userId"
            name="user_id"
            autocomplete="off"
            class="visually-hidden opacity-0"
            type="text"
          />
        </FormKit>
      </div>
      <div
        v-else-if="submitting && !submitted"
        class="form-inner flex items-center justify-center"
      >
        <LottieAnimation
          path="./themes/custom/eyedea/assets/lottie/circular-loader.json"
          :auto-play="true"
          :loop="true"
          :height="400"
          :width="400"
        />
      </div>
      <div
        v-else-if="!submitting && submitError"
        class="form-inner flex flex-col my-10"
      >
        <h2 class="~text-xl/3xl text-danger mb-5">
          {{ errorHeadline }}
        </h2>
        <p
          class="mb-5 ~text-sm/base text-start"
          v-html="errorMessage"
        />
      </div>

      <div
        v-else-if="!submitting && submitted"
        class="form-inner flex flex-col justify-center items-center my-10"
      >
        <LottieAnimation
          path="./themes/custom/eyedea/assets/lottie/success-check.json"
          :auto-play="true"
          :loop="false"
          :height="200"
          :width="200"
        />
        <h2 class="~text-xl/4xl text-primary font-bold mb-5">
          {{ successHeadline }}
        </h2>
        <p class="mb-5 ~text-base/xl text-center">
          {{ successMessage }}
        </p>
      </div>
  </div>
</template>

<style lang="scss" scoped>
</style>
